import { useMemo } from 'react';
import Select, {
  components,
  MenuProps,
  OptionProps,
  GroupBase,
  PlaceholderProps,
} from 'react-select';
import clsx from 'clsx';
import { Checkbox } from '@headlessui/react';

import { Account } from '~/shared/utils/types';
import CheckIcon from '~/shared/assets/icons/check.svg';
import CloseIcon from '~/shared/assets/icons/close.svg';

import {
  DropdownIndicator,
  MultiValue,
  type OptionType,
} from './InputAutocompleteMultiSelect/components';
import { selectStyles } from './InputAutocompleteMultiSelect/styles';

// Custom Menu component with Select All and Clear Selection
const CustomMenu = (props: MenuProps<OptionType, true, GroupBase<OptionType>>) => {
  const { selectProps } = props;
  const { value, options, onChange } = selectProps;

  // Flatten options in case they contain groups
  const flatOptions = useMemo(() => {
    return options.flatMap((option) =>
      'options' in option ? option.options : [option]
    ) as OptionType[];
  }, [options]);

  const selectedCount = Array.isArray(value) ? value.length : 0;
  const hasSelection = selectedCount > 0;
  const allSelected = selectedCount === flatOptions.length;

  const handleSelectAll = () => {
    if (onChange) {
      onChange(flatOptions, {
        action: 'select-option',
        option: undefined,
      });
    }
  };

  const handleClearAll = () => {
    if (onChange) {
      onChange([], {
        action: 'clear',
        removedValues: value as readonly OptionType[],
      });
    }
  };

  return (
    <components.Menu {...props}>
      <div className="sticky top-0 z-10 bg-white px-3 py-2 flex justify-between">
        <button
          onClick={handleSelectAll}
          disabled={allSelected}
          className={clsx(
            'text-xs font-semibold font-family-inter',
            allSelected ? 'text-gray-400 cursor-not-allowed' : 'text-gray-900 cursor-pointer'
          )}
          type="button"
        >
          Select All
        </button>
        <button
          onClick={handleClearAll}
          disabled={!hasSelection}
          className={clsx(
            'text-xs font-semibold font-family-inter flex gap-1',
            !hasSelection ? 'text-gray-400 cursor-not-allowed' : 'text-gray-900 cursor-pointer'
          )}
          type="button"
        >
          <CloseIcon
            className={clsx(
              'size-2.5 my-auto',
              !hasSelection ? 'text-gray-400 cursor-not-allowed' : 'text-gray-900 cursor-pointer'
            )}
          />
          Clear Selection
        </button>
      </div>
      {props.children}
    </components.Menu>
  );
};

// Custom Option component with InputAutocompleteMultiSelect styling
const CustomOption = (props: OptionProps<OptionType, true, GroupBase<OptionType>>) => {
  const { isSelected, label, innerProps, innerRef, isDisabled } = props;
  const { ...restInnerProps } = innerProps as {
    className?: string;
    style?: React.CSSProperties;
    [key: string]: unknown;
  };

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (isDisabled) {
      return;
    }
    if (isSelected) {
      e.preventDefault();
      e.stopPropagation();
      const selected = (props.selectProps.value ?? []) as OptionType[];
      const next = selected.filter((v) => v.value !== (props.data as OptionType).value);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (props.selectProps as any).onChange?.(next, {
        action: 'deselect-option',
        option: props.data,
      });
      return;
    }
    // fall back to default behavior for selecting
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (restInnerProps as any).onMouseDown?.(e);
  };

  return (
    <div
      ref={innerRef}
      {...restInnerProps}
      onMouseDown={handleMouseDown}
      className={clsx(
        'text-sm leading-6 w-full h-12 2xl:h-14 flex items-center justify-between px-5 py-[18px] border-b border-gray-200 first:rounded-t-lg last:rounded-b-lg group',
        isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-20 cursor-pointer'
      )}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={isSelected}
          className="group block size-5 p-[4px] rounded-md border border-primary-100 bg-white data-checked:bg-primary-500 ring-0 active:ring-primary-300 focus:ring-primary-500 cursor-pointer"
        >
          <CheckIcon className="text-white stroke-2 size-2.5 opacity-0 group-data-checked:opacity-100 transition duration-200 ease-in-out" />
        </Checkbox>
        <span className="text-primary-900">{label}</span>
      </div>
    </div>
  );
};

const CustomPlaceholder = (props: PlaceholderProps<OptionType, true, GroupBase<OptionType>>) => {
  return (
    <components.Placeholder {...props}>
      <span className="text-gray-900">{props.children}</span>
    </components.Placeholder>
  );
};

export default function AccountInputMultiSelect({
  accounts,
  values,
  onChange,
  placeholder = 'All accounts',
  className,
  disabled = false,
}: {
  accounts: Account[];
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  // Convert accounts to option format
  const accountOptions = useMemo<OptionType[]>(
    () =>
      accounts.map((account) => ({
        value: account.accountId,
        label: account.accountName,
      })),
    [accounts]
  );

  // Convert selected account IDs to options
  const valueOptions = useMemo<OptionType[]>(
    () =>
      values.map((accountId) => {
        const account = accounts.find((acc) => acc.accountId === accountId);
        return {
          value: accountId,
          label: account ? account.accountName : accountId,
        };
      }),
    [values, accounts]
  );

  const handleChange = (opts: readonly OptionType[] | null) => {
    onChange((opts || []).map((o) => o.value));
  };

  return (
    <Select<OptionType, true>
      isMulti
      isSearchable={false}
      isDisabled={disabled}
      options={accountOptions}
      value={valueOptions}
      onChange={handleChange}
      placeholder={placeholder}
      className={clsx(className)}
      components={{
        DropdownIndicator,
        IndicatorSeparator: null,
        MultiValue,
        Placeholder: CustomPlaceholder,
        Menu: CustomMenu,
        Option: CustomOption,
      }}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      loadingMessage={() => 'Loading accounts...'}
      noOptionsMessage={() => 'No accounts found'}
      styles={selectStyles}
      classNamePrefix="dfy-select"
    />
  );
}
